import { useState, useEffect, useRef, useCallback } from "react";
import { Facebook, Link2, Check, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DEFAULT_VIDEO_URL = "https://www.facebook.com/share/v/1ES8qtY88A/";

const FacebookLive = () => {
  const [videoUrl, setVideoUrl] = useState(DEFAULT_VIDEO_URL);
  const [inputUrl, setInputUrl] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [urlApplied, setUrlApplied] = useState(false);
  const containerRef = useRef(null);
  const sdkLoaded = useRef(false);

  const loadFBSDK = useCallback(() => {
    if (window.FB) {
      window.FB.XFBML.parse(containerRef.current);
      return;
    }

    if (sdkLoaded.current) return;
    sdkLoaded.current = true;

    // Create fb-root if not exists
    if (!document.getElementById("fb-root")) {
      const fbRoot = document.createElement("div");
      fbRoot.id = "fb-root";
      document.body.prepend(fbRoot);
    }

    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v18.0";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onload = () => {
      if (window.FB) {
        window.FB.XFBML.parse(containerRef.current);
      }
    };
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    loadFBSDK();
  }, [loadFBSDK]);

  useEffect(() => {
    // Re-parse when videoUrl changes
    const timeout = setTimeout(() => {
      if (window.FB && containerRef.current) {
        window.FB.XFBML.parse(containerRef.current);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [videoUrl]);

  const handleApplyUrl = () => {
    if (!inputUrl.trim()) return;
    // Basic validation for Facebook video URL
    if (inputUrl.includes("facebook.com") && (inputUrl.includes("/videos/") || inputUrl.includes("/watch") || inputUrl.includes("fb.watch"))) {
      setVideoUrl(inputUrl.trim());
      setUrlApplied(true);
      setTimeout(() => {
        setUrlApplied(false);
        setShowInput(false);
        setInputUrl("");
      }, 1500);
    }
  };

  return (
    <div className="mt-8 card-hard-shadow bg-white overflow-hidden" data-testid="facebook-live-section">
      {/* Header */}
      <div className="p-6 border-b border-zinc-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 flex items-center justify-center">
            <Facebook size={24} className="text-white" />
          </div>
          <div>
            <h3 className="font-['Clash_Display'] font-semibold text-xl">
              Facebook Live
            </h3>
            <p className="font-['Satoshi'] text-zinc-500 text-sm">
              Watch our live broadcasts
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!showInput ? (
            <Button
              onClick={() => setShowInput(true)}
              variant="outline"
              className="btn-sharp text-sm flex items-center gap-2 px-4 py-2"
              data-testid="add-url-btn"
            >
              <Plus size={16} /> Add Video URL
            </Button>
          ) : (
            <div className="flex items-center gap-2" data-testid="url-input-area">
              <div className="relative flex-1">
                <Link2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  type="url"
                  value={inputUrl}
                  onChange={(e) => setInputUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleApplyUrl()}
                  placeholder="Paste Facebook video URL..."
                  className="input-sharp h-10 pl-9 text-sm w-64"
                  data-testid="fb-url-input"
                />
              </div>
              <Button
                onClick={handleApplyUrl}
                className={`btn-sharp text-sm px-4 py-2 ${urlApplied ? "bg-green-600" : "bg-black"} text-white`}
                data-testid="apply-url-btn"
              >
                {urlApplied ? <Check size={16} /> : "Load"}
              </Button>
              <button
                onClick={() => { setShowInput(false); setInputUrl(""); }}
                className="text-zinc-400 hover:text-black transition-colors"
                data-testid="close-url-btn"
              >
                <X size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Video Player */}
      <div ref={containerRef} className="bg-black" data-testid="fb-video-container">
        <div
          className="fb-video"
          data-href={videoUrl}
          data-width="auto"
          data-show-text="false"
          data-allowfullscreen="true"
          data-autoplay="false"
        />
      </div>

      {/* Footer */}
      <div className="p-4 bg-zinc-50 flex items-center justify-between">
        <p className="font-['Satoshi'] text-xs text-zinc-400 truncate max-w-[70%]">
          {videoUrl}
        </p>
        <a
          href="https://www.facebook.com/LightCityNola"
          target="_blank"
          rel="noopener noreferrer"
          className="font-['Satoshi'] text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
          data-testid="fb-page-link"
        >
          Visit Facebook Page
        </a>
      </div>
    </div>
  );
};

export default FacebookLive;
